import {
  type InferOutput,
  array,
  boolean,
  fallback,
  nullish,
  number,
  object,
  optional,
  picklist,
  record,
  string,
} from "valibot";

export const RegistrationStatusSchema = fallback(
  optional(picklist(["open", "closed", "soon", "onDemand"]), "closed"),
  "closed",
);

export const BaseSearchTravelSchema = object({
  objectID: string(),
  id: number(),
  title: string(),
  subtitle: string(),
  alias: nullish(string()),
  travelTypeId: string(),
  rythme: nullish(number()),
  teaser: nullish(string()),
  introduction: nullish(string()),
  brandTitle: nullish(string()),
  brandCustomerTitle: nullish(string()),
  boost: number(),
  sponsored: boolean(),
  date: nullish(number()),
  price: nullish(number()),
  budgets: nullish(picklist(["lte2000", "lte3000", "lte5000", "gt5000"])),
  travelImages: array(
    object({
      id: number(),
      realname: string(),
      name: string(),
      caption: string(),
    }),
  ),
  durations: array(picklist(["WEEKEND", "BREAK", "LONG"])),
  primo: boolean(),
  garanti: boolean(),
  lastMinute: boolean(),
  startMonths: array(string()),
  themes: array(string()),
  themeIds: array(number()),
  destinations: array(string()),
  destinationAliases: array(string()),
  destinationIds: array(number()),
  speakers: array(string()),
  speakerIds: array(number()),
  destinationTravels: array(
    object({
      id: number(),
      title: string(),
      alias: nullish(string()),
    }),
  ),
  departures: array(
    object({
      id: number(),
      dateStart: string(),
      dateEnd: string(),
      dateStartSec: number(),
      dateEndSec: number(),
      defaultPrice: number(),
      primoPrice: nullish(number()),
      nights: nullish(number()),
      days: nullish(number()),
      garanti: boolean(),
      untilGaranti: nullish(number()),
      paxMin: nullish(number()),
      versionId: number(),
      speakerType: nullish(string()),
      speakers: array(
        object({
          id: number(),
          alias: nullish(string()),
          firstname: nullish(string()),
          lastname: nullish(string()),
          imageName: nullish(string()),
          imageRealname: nullish(string()),
          pickupLine: nullish(string()),
        }),
      ),
      state: object({
        registrationStatus: RegistrationStatusSchema,
        versionStatus: RegistrationStatusSchema,
        paxAvailable: number(),
        paxUsed: number(),
      }),
      infosupUrl: string(),
    }),
  ),
  departuresDateStart: record(string(), number()),
  departuresDateEnd: record(string(), number()),
  markers: array(
    object({
      lat: number(),
      lng: number(),
    }),
  ),
  hasDepartures: boolean(),
  isFitTemplate: fallback(optional(boolean()), false),
  days: nullish(number()),
  nights: nullish(number()),
});

export const SearchTravelSchema = object({
  ...BaseSearchTravelSchema.entries,
  _highlightResult: object({
    title: object({
      value: string(),
    }),
  }),
});

export const SearchResultSchema = object({
  queryID: optional(string()),
  hits: array(SearchTravelSchema),
  nbHits: number(),
  facets: object({
    themes: optional(record(string(), number())),
    destinations: optional(record(string(), number())),
    speakers: optional(record(string(), number())),
    speakerIds: optional(record(string(), number())),
    durations: optional(record(string(), number())),
    startMonths: optional(record(string(), number())),
    rythme: optional(record(string(), number())),
    budgets: optional(record(string(), number())),
  }),
});

export const SearchDataSchema = object({
  queryId: optional(string()),
  travels: array(SearchTravelSchema),
  fitTravels: fallback(optional(array(SearchTravelSchema), []), []),
  totalTravels: number(),
  durations: array(object({ key: string(), count: number() })),
  themes: array(object({ key: string(), count: number() })),
  destinations: array(object({ key: string(), count: number() })),
  speakers: array(object({ key: string(), count: number() })),
  speakerIds: array(object({ key: number(), count: number() })),
  startMonths: array(object({ key: string(), count: number() })),
  budgets: array(object({ key: string(), count: number() })),
  rythmes: array(object({ key: string(), count: number() })),
  suggestions: array(string()),
});

export type SearchData = InferOutput<typeof SearchDataSchema>;
export type SearchTravel = InferOutput<typeof SearchTravelSchema>;
export type SearchDepartures = SearchTravel["departures"];
export type SearchDeparture = SearchDepartures[number];
export type SearchSpeaker = SearchDeparture["speakers"][number];
